import { useCallback } from 'react'
import { FormsFormValues } from './formsForm.constants'
import { getFileExtension } from '@/pages/documents/docHelper'
import { WORD_MIME_TYPES, EXCEL_MIME_TYPES, POWERPOINT_MIME_TYPES } from './formsForm.constants'

interface UseFormsFormHandlersProps {
  setFieldValue: (field: keyof FormsFormValues, value: any) => void
  values: FormsFormValues
  onSnackbar: (message: string, severity: 'success' | 'error' | 'warning') => void
}

export const validateFileType = (file: File, selectedType: string): { valid: boolean; message: string } => {
  const extension = getFileExtension(file.name).replace('.', '').toLowerCase()

  switch (selectedType) {
    case 'pdf':
      if (extension === 'pdf' && file.type === 'application/pdf') {
        return { valid: true, message: '' }
      }
      return { valid: false, message: 'Debes subir un archivo PDF (.pdf)' }

    case 'docx':
      if (['doc', 'docx', ''].includes(extension) && WORD_MIME_TYPES.includes(file.type)) {
        return { valid: true, message: '' }
      }
      return { valid: false, message: 'Debes subir un archivo de Word (.doc o .docx)' }

    case 'xlsx':
      if (['xls', 'xlsx'].includes(extension) && EXCEL_MIME_TYPES.includes(file.type)) {
        return { valid: true, message: '' }
      }
      return { valid: false, message: 'Debes subir un archivo de Excel (.xls o .xlsx)' }

    case 'pptx':
      if (['ppt', 'pptx'].includes(extension) && POWERPOINT_MIME_TYPES.includes(file.type)) {
        return { valid: true, message: '' }
      }
      return { valid: false, message: 'Debes subir un archivo de PowerPoint (.ppt o .pptx)' }

    default:
      return { valid: true, message: '' }
  }
}

export const getAcceptedFileTypes = (selectedType: string): string => {
  switch (selectedType) {
    case 'pdf':
      return '.pdf'
    case 'docx':
      return '.doc,.docx'
    case 'xlsx':
      return '.xlsx,.xls'
    case 'pptx':
      return '.pptx,.ppt'
    default:
      return '.pdf,.doc,.docx'
  }
}

export const useFormsFormHandlers = ({ setFieldValue, values, onSnackbar }: UseFormsFormHandlersProps) => {
  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFieldValue('name', e.target.value)
    },
    [setFieldValue]
  )

  const handleTypeChange = useCallback(
    (newType: string) => {
      const currentDoc = values.document
      if (currentDoc && currentDoc instanceof File) {
        const validation = validateFileType(currentDoc, newType)

        if (!validation.valid) {
          onSnackbar(
            `El archivo actual no es compatible con ${newType}. Por favor, sube un archivo nuevo.`,
            'warning'
          )
          setFieldValue('document', null)
        }
      }

      setFieldValue('type', newType)
    },
    [setFieldValue, values.document, onSnackbar]
  )

  const handleDocumentSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const f = e.target.files?.[0] || null

      if (!f) {
        setFieldValue('document', null)
        return
      }

      if (!values.type) {
        onSnackbar('Por favor selecciona primero el tipo de documento', 'warning')
        e.target.value = ''
        return
      }

      const validation = validateFileType(f, values.type)
      if (!validation.valid) {
        onSnackbar(validation.message, 'error')
        e.target.value = ''
        return
      }

      setFieldValue('document', f)
      onSnackbar('Archivo cargado correctamente', 'success')
    },
    [setFieldValue, values.type, onSnackbar]
  )

  const handleRemoveDocument = useCallback(() => {
    setFieldValue('document', null)
  }, [setFieldValue])

  return {
    handleNameChange,
    handleTypeChange,
    handleDocumentSelect,
    handleRemoveDocument,
  }
}
